import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import AiSparklesIcon from '@upright/ui/icons/ai-sparkles-streamline-core';
import { useAskAiEntryPoint } from '@upright/ui/chat/use-ask-ai-entry-point';
import { useOpenAskAiPanel } from '@upright/ui/chat/use-open-ask-ai-panel';
import styled from 'styled-components';
import { getTextStyle } from '../../../../content/texts/bou-text/text-style';
import { BouColor } from '../../../../global/bou-colors';
import { navFastTransition } from './shared';

/**
 * Top-bar entry point for the AI chat. Visible only when the caller
 * holds the `USE_AI_CHAT_WEB` resourceless privilege (team users always
 * pass; other orgs are toggled on per-customer from the org admin view).
 *
 * Hidden — not greyed out — when access isn't granted, per the design
 * decision to skip an upsell tooltip until the feature has stabilised.
 * `useHasResourcelessPrivilege` short-circuits anonymous visitors so the
 * public site doesn't fire a 401 on every page load.
 *
 * PLT-5041 / PLT-5235 (option C): for a MULTI-company DMA Consultant user,
 * chat is only available inside a company. Off-company the entry point is
 * HIDDEN entirely (not merely disabled) so every chat a consultant can start
 * carries a client company and is metered to it. Inside a company (the page
 * published a `company` chat binding) it behaves normally and the bound
 * company id is sent with the request (see `upright-chat-adapter.ts`), so
 * tokens are attributed to that client company. Single-company consultants
 * and all other org types are unaffected. This shared gate / URL logic lives
 * in `useAskAiEntryPoint`, which the DMA-header and mobile entry points use too.
 *
 * Plain-click opens the right-side floating chat panel (PLT-4625). The
 * panel mounts at the app root and shows the current thread (or the
 * empty welcome state if no prior conversation), so the user can ask a
 * question without leaving whatever page they're on. Modifier-clicks
 * (cmd / ctrl / middle / shift) fall through to native navigation and
 * open `/chat` in a new tab — preserving the previous "open in full
 * page" affordance for power users. That `/chat` URL carries the page's
 * entity context — company, fund or portfolio — so a new-tab open opens about
 * the same entity as the in-panel open, and on a company is attributed to it
 * as well (option C). The URL used to be gated to companies, so a modifier
 * click on a fund page silently dropped the fund (PLT-6554).
 *
 * If the user is on an entity-aware page (Company, CompanyGroup,
 * Portfolio — see `usePublishCurrentPageChatBinding` callers) we
 * additionally pre-apply that entity's binding and a page-context chip
 * so the chat opens with the correct entity in mind — instead of the
 * generic global welcome state (PLT-4665). Non-entity pages open the
 * plain welcome state. Either way the panel starts a new conversation.
 */
export function AskAiLink() {
  const { hidden, binding, href } = useAskAiEntryPoint('public');
  const handleClick = useOpenAskAiPanel(binding);
  if (hidden) {
    return null;
  }

  return (
    <StyledLink data-testid={E2eTestElementId.ASK_AI_LINK} href={href} onClick={handleClick}>
      <IconWrap>
        <AiSparklesIcon width="1em" height="1em" />
      </IconWrap>
      <span>Ask AI</span>
    </StyledLink>
  );
}

/**
 * The Ask AI entry point is a filled brand-yellow pill rather than the plain
 * white nav-text label the sibling items use, so it stands out on the
 * near-black investor header and draws clicks (PLT-5553). The loud tier of the
 * brand yellow is a sanctioned chat-entry-point use of the accent — see
 * DESIGN.md §"Brand yellow marks an AI entry point". Dark text + icon on
 * yellow, with the hover stepping up to the loud tier's hover token, mirrors
 * the chat composer send button so the entry point and its destination stay
 * visually linked.
 */
const StyledLink = styled.a`
  ${getTextStyle({ size: 'xxxs' })}
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.45em 0.9em;
  border-radius: 999px;
  background-color: var(--color__brand-yellow-loud);
  color: ${BouColor.PLATFORM_BG_BLACK};
  text-decoration: none;
  cursor: pointer;
  transition: background-color ${navFastTransition};
  &:hover {
    background-color: var(--color__brand-yellow-loud-hover);
  }
  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: ${BouColor.PLATFORM_BG_BLACK};
    text-decoration: none;
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
`;
